import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Landmark, CheckCircle2, ArrowLeft, Filter, RefreshCw, ShieldCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

const STATO_LABEL = {
  da_verificare: "Da verificare",
  riconciliato: "Riconciliato",
  tutti: "Tutti",
};

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}
function formatDateIT(iso) {
  if (!iso) return "—";
  try {
    const [y, m, d] = String(iso).split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
}
function todayISO() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Controllo C/C — riconciliazione bonifici.
 * Accessibile a: agente (agenzia intera) e collaboratore con conto_separato + opt-in.
 */
export default function ControlloCC() {
  const { user, refreshMe } = useAuth();
  const isAgente = user?.role === "agente" || user?.role === "admin" || user?.role === "consultazione";
  const isCollab = user?.role === "collaboratore";
  const collabNeedsOptIn = isCollab && !!user?.conto_separato && !user?.controllo_cc_enabled;
  const backTo = isAgente ? "/admin" : "/";

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ n_da_verificare: 0, n_riconciliati: 0, importo_da_verificare: 0, importo_riconciliato: 0 });
  const [loading, setLoading] = useState(true);
  const [stato, setStato] = useState("da_verificare");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [dialog, setDialog] = useState({ open: false, mode: "single", entryIds: [], data_accredito: todayISO(), note_cc: "", submitting: false });
  const [activating, setActivating] = useState(false);
  const [selected, setSelected] = useState(new Set()); // multi-selezione

  const load = useCallback(async () => {
    if (collabNeedsOptIn) { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("stato", stato);
      if (fromDate) params.set("from_date", fromDate);
      if (toDate) params.set("to_date", toDate);
      const { data } = await api.get(`/controllo-cc/entries?${params.toString()}`);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setSummary(data?.summary || {});
      setSelected(new Set()); // reset selezione ad ogni fetch
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [stato, fromDate, toDate, collabNeedsOptIn]);

  useEffect(() => { load(); }, [load]);

  // Righe selezionabili (escluse auto-riconciliate: non modificabili)
  const selectableItems = useMemo(() => items.filter((r) => !r.auto_riconciliato), [items]);
  const selectedItems = useMemo(() => items.filter((r) => selected.has(r.id)), [items, selected]);
  const selectedTotal = useMemo(() => selectedItems.reduce((s, r) => s + Number(r.importo || 0), 0), [selectedItems]);
  // Se tutte le selezionate sono già riconciliate → azione = "annulla riconciliazione"
  const bulkMode = useMemo(() => {
    if (selectedItems.length === 0) return "none";
    const allRic = selectedItems.every((r) => r.riconciliato_cc);
    return allRic ? "unreconcile" : "reconcile";
  }, [selectedItems]);
  const headerCheckboxState = useMemo(() => {
    if (selectableItems.length === 0) return false;
    const sel = selectableItems.filter((r) => selected.has(r.id)).length;
    if (sel === 0) return false;
    if (sel === selectableItems.length) return true;
    return "indeterminate";
  }, [selectableItems, selected]);

  const toggleRowSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleHeader = () => {
    if (headerCheckboxState === true) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableItems.map((r) => r.id)));
    }
  };
  const clearSelection = () => setSelected(new Set());

  const openBulkConfirm = () => {
    setDialog({
      open: true,
      mode: bulkMode,
      entryIds: Array.from(selected),
      data_accredito: todayISO(),
      note_cc: "",
      submitting: false,
    });
  };

  const submitBulk = async () => {
    setDialog((d) => ({ ...d, submitting: true }));
    const toReconcile = dialog.mode === "reconcile";
    try {
      const { data } = await api.post(`/controllo-cc/entries/bulk-toggle`, {
        entry_ids: dialog.entryIds,
        riconciliato: toReconcile,
        data_accredito: toReconcile ? (dialog.data_accredito || null) : null,
        note_cc: toReconcile ? dialog.note_cc : null,
      });
      const upd = data?.updated ?? 0;
      const skp = data?.skipped ?? 0;
      if (upd > 0) {
        toast.success(
          toReconcile
            ? `${upd} bonific${upd === 1 ? "o segnato" : "i segnati"} come accreditat${upd === 1 ? "o" : "i"}${skp ? ` (${skp} salt${skp === 1 ? "ato" : "ati"})` : ""}`
            : `${upd} riconciliazion${upd === 1 ? "e annullata" : "i annullate"}${skp ? ` (${skp} saltate)` : ""}`,
        );
      } else {
        toast.warning(`Nessuna riga aggiornata (${skp} saltate)`);
      }
      setDialog({ open: false, mode: "single", entryIds: [], data_accredito: todayISO(), note_cc: "", submitting: false });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
      setDialog((d) => ({ ...d, submitting: false }));
    }
  };

  const totalShown = useMemo(
    () => items.reduce((s, r) => s + Number(r.importo || 0), 0),
    [items],
  );

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="controllo-cc-page">
      <div className="flex items-center gap-3">
        <Link to={backTo} className="text-slate-500 hover:text-slate-700" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div>
          <div className="text-xs uppercase tracking-wider text-blue-600 font-semibold">Contabilità</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Landmark className="w-6 h-6 text-blue-600" />
            Controllo C/C
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isAgente
              ? "Riconcilia i bonifici in entrata: spunta quelli effettivamente accreditati sul conto agenzia."
              : "Riconcilia i tuoi bonifici in entrata sul tuo conto separato."}
          </p>
        </div>
      </div>

      {collabNeedsOptIn && (
        <Card className="p-6 border-blue-200 bg-blue-50/40" data-testid="cc-optin-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <Landmark className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-lg font-bold text-slate-900">Attiva il Controllo C/C personale</h2>
              <p className="text-sm text-slate-600 mt-1">
                Poiché hai il <b>conto separato</b> attivo, puoi abilitare una sezione dedicata dove monitorare tutti i tuoi bonifici in entrata e spuntarli come effettivamente accreditati sul tuo conto corrente. È uno strumento personale per tenere sotto controllo la tua contabilità.
              </p>
              <Button
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white"
                disabled={activating}
                onClick={async () => {
                  setActivating(true);
                  try {
                    await api.put("/controllo-cc/settings/self", { enabled: true });
                    toast.success("Controllo C/C attivato");
                    await refreshMe?.();
                  } catch (e) {
                    toast.error(formatApiError(e));
                  } finally {
                    setActivating(false);
                  }
                }}
                data-testid="cc-optin-activate"
              >
                {activating ? "Attivo…" : "Attiva Controllo C/C"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {!collabNeedsOptIn && (
      <>
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="p-4 border-amber-200 bg-amber-50/50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Da verificare</div>
              <div className="text-2xl font-heading font-bold text-amber-900 mt-1" data-testid="sum-da-verificare">
                {formatEUR(summary.importo_da_verificare || 0)}
              </div>
              <div className="text-xs text-amber-700 mt-0.5">{summary.n_da_verificare || 0} bonifici in attesa</div>
            </div>
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Filter className="w-5 h-5 text-amber-700" />
            </div>
          </div>
        </Card>
        <Card className="p-4 border-emerald-200 bg-emerald-50/50">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Già accreditati</div>
              <div className="text-2xl font-heading font-bold text-emerald-900 mt-1" data-testid="sum-riconciliati">
                {formatEUR(summary.importo_riconciliato || 0)}
              </div>
              <div className="text-xs text-emerald-700 mt-0.5">{summary.n_riconciliati || 0} bonifici confermati</div>
            </div>
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-emerald-700" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filtri */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Stato</Label>
            <div className="flex gap-1 mt-1">
              {["da_verificare", "riconciliato", "tutti"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStato(s)}
                  className={`flex-1 px-2 py-1.5 text-xs rounded border transition ${stato === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}
                  data-testid={`filter-stato-${s}`}
                >
                  {STATO_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Data incasso — dal</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1" data-testid="filter-from" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">al</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1" data-testid="filter-to" />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => { setFromDate(""); setToDate(""); }} data-testid="filter-reset">
              Azzera date
            </Button>
            <Button variant="outline" size="sm" onClick={load} data-testid="filter-refresh">
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
          </div>
        </div>
      </Card>

      {/* Toolbar bulk — visibile quando c'è almeno 1 selezione */}
      {selected.size > 0 && (
        <Card className="p-3 border-blue-300 bg-blue-50/60 flex items-center justify-between gap-3 flex-wrap" data-testid="cc-bulk-toolbar">
          <div className="text-sm text-slate-800">
            <b>{selected.size}</b> voc{selected.size === 1 ? "e selezionata" : "i selezionate"} · Totale:
            {" "}<span className="font-mono font-semibold">{formatEUR(selectedTotal)}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={clearSelection} data-testid="cc-bulk-clear">
              Deseleziona
            </Button>
            {bulkMode === "reconcile" && (
              <Button
                size="sm"
                onClick={openBulkConfirm}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="cc-bulk-reconcile"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Segna {selected.size} come accreditat{selected.size === 1 ? "o" : "i"}
              </Button>
            )}
            {bulkMode === "unreconcile" && (
              <Button
                size="sm"
                variant="outline"
                onClick={openBulkConfirm}
                data-testid="cc-bulk-unreconcile"
              >
                Annulla riconciliazione di {selected.size}
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Lista */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left border-b border-slate-200">
                <th className="w-12 px-2 py-3">
                  <Checkbox
                    checked={headerCheckboxState === true}
                    onCheckedChange={toggleHeader}
                    aria-label="Seleziona tutto"
                    disabled={selectableItems.length === 0}
                    data-testid="cc-check-header"
                  />
                </th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Data incasso</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Contraente</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">N. polizza</th>
                {isAgente && (
                  <th className="px-3 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Collaboratore</th>
                )}
                <th className="px-3 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold text-right">Importo</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Data accredito</th>
                <th className="px-3 py-3 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Stato</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={isAgente ? 8 : 7} className="text-center py-10 text-slate-500">
                    <Landmark className="w-6 h-6 mx-auto mb-2 text-slate-300" />
                    Nessun bonifico {stato === "da_verificare" ? "da verificare" : stato === "riconciliato" ? "riconciliato" : ""} in questo periodo.
                  </td>
                </tr>
              )}
              {items.map((r) => {
                const isSelected = selected.has(r.id);
                const isReconciled = r.riconciliato_cc || r.auto_riconciliato;
                return (
                  <tr
                    key={r.id}
                    className={`hover:bg-slate-50 ${isSelected ? "bg-blue-50/60" : ""}`}
                    data-testid={`cc-row-${r.id}`}
                  >
                    <td className="pl-3 py-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => {
                          if (r.auto_riconciliato) return;
                          toggleRowSelect(r.id);
                        }}
                        disabled={r.auto_riconciliato}
                        title={r.auto_riconciliato ? "Auto-riconciliato (versamento convalidato) — non modificabile" : "Seleziona per riconciliare o annullare"}
                        data-testid={`cc-check-${r.id}`}
                      />
                    </td>
                    <td className="px-3 py-3 text-slate-700 whitespace-nowrap">{formatDateIT(r.data_incasso)}</td>
                    <td className="px-3 py-3 text-slate-900 font-medium">{r.contraente || "—"}</td>
                    <td className="px-3 py-3 text-slate-600">{r.numero_polizza || "—"}</td>
                    {isAgente && <td className="px-3 py-3 text-slate-600">{r.collaboratore}</td>}
                    <td className="px-3 py-3 text-right font-mono text-slate-900">{formatEUR(r.importo)}</td>
                    <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{r.data_accredito ? formatDateIT(r.data_accredito) : "—"}</td>
                    <td className="px-3 py-3">
                      {r.auto_riconciliato ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5" title="Riconciliato automaticamente dal versamento convalidato">
                          <CheckCircle2 className="w-3 h-3" /> Auto
                        </span>
                      ) : r.riconciliato_cc ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                          <CheckCircle2 className="w-3 h-3" /> Accreditato
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 rounded-full px-2 py-0.5">
                          Da verificare
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {items.length > 0 && (
              <tfoot>
                <tr className="bg-slate-50 border-t border-slate-200 font-semibold">
                  <td colSpan={isAgente ? 5 : 4} className="px-3 py-3 text-slate-700">
                    Totale voci mostrate: {items.length}
                  </td>
                  <td className="px-3 py-3 text-right font-mono text-slate-900">{formatEUR(totalShown)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {/* Dialog conferma bulk con data accredito + note comuni */}
      <Dialog open={dialog.open} onOpenChange={(o) => !o && setDialog((d) => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-md" data-testid="cc-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className={`w-5 h-5 ${dialog.mode === "reconcile" ? "text-emerald-600" : "text-slate-600"}`} />
              {dialog.mode === "reconcile"
                ? `Conferma ${dialog.entryIds.length} bonific${dialog.entryIds.length === 1 ? "o accreditato" : "i accreditati"}`
                : `Annulla riconciliazione di ${dialog.entryIds.length}`}
            </DialogTitle>
            <DialogDescription>
              {dialog.mode === "reconcile"
                ? "La stessa data di accredito e nota verranno applicate a tutte le voci selezionate."
                : "Le voci selezionate torneranno nello stato \"Da verificare\"."}
            </DialogDescription>
          </DialogHeader>
          {dialog.mode === "reconcile" && (
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Data effettiva di accredito</Label>
                <Input
                  type="date"
                  value={dialog.data_accredito}
                  onChange={(e) => setDialog((d) => ({ ...d, data_accredito: e.target.value }))}
                  className="mt-1"
                  data-testid="cc-data-accredito"
                />
                <p className="text-xs text-slate-500 mt-1">Puoi lasciare la data di oggi o inserire la data effettiva dell&apos;estratto conto.</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Nota comune (opzionale)</Label>
                <Input
                  placeholder="Es. bonifico cumulativo di due polizze, arrivato oggi…"
                  value={dialog.note_cc}
                  onChange={(e) => setDialog((d) => ({ ...d, note_cc: e.target.value }))}
                  className="mt-1"
                  data-testid="cc-note"
                />
              </div>
              <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-xs text-slate-600">
                Voci coinvolte: <b>{dialog.entryIds.length}</b> · Totale: <b className="font-mono">{formatEUR(selectedTotal)}</b>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDialog((d) => ({ ...d, open: false }))} data-testid="cc-confirm-cancel">
              Annulla
            </Button>
            <Button
              onClick={submitBulk}
              disabled={dialog.submitting}
              className={dialog.mode === "reconcile" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
              data-testid="cc-confirm-ok"
            >
              {dialog.mode === "reconcile" && <CheckCircle2 className="w-4 h-4 mr-2" />}
              {dialog.submitting
                ? "Salvo…"
                : dialog.mode === "reconcile" ? "Conferma" : "Annulla riconciliazione"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
